import React from 'react'
import moment from 'moment'
import { startCase, toNumber } from 'lodash'
import { IconSparkles } from '@tabler/icons-react'
import pluralize from 'pluralize'

import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { Pill } from '@/components/pills/Pill'
import {
  COACHING_TASK_TYPE,
  READY_FOR_REVIEW,
  TASK_STATUS_MAP,
  COMPLETED,
  IGNORED,
} from '@/reducers/tasks/tasks.constants'
import { determineGradient } from '@/components/helpers'
import { dateFormatMoment } from '@/utils/constants'
import { PoweredByAI } from '@/components/ai/PoweredByAI'

import './TaskCard.scss'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

export const TaskCard = ({
  title,
  status,
  type,
  isAiGenerated,
  agentReviewed,
  createdBy,
  agent,
  color,
  description,
  callCount,
  createdDate,
  updatedDate,
  handlePrimaryAction,
  primaryActionButtonText,
  handleSecondaryAction,
  secondaryActionButtonText,
  index,
  dataTestId,
  callType = 'call',
  selectable,
  selected,
  onSelect,
}) => {
  const created = moment.utc(createdDate).local()
  const updated = moment.utc(updatedDate).local()
  const now = moment()
  const hasBeenUpdated = !created.isSame(updated)
  const dateToUse = hasBeenUpdated ? updated : created
  const displayDate =
    now.diff(dateToUse, 'hours') < 24
      ? dateToUse.fromNow()
      : dateToUse.format(dateFormatMoment.long)
  const isArchived = status !== READY_FOR_REVIEW

  return (
    <div
      className="task-card"
      data-testid={`task-card-${dataTestId}`}
      onClick={handlePrimaryAction}
    >
      <header>
        <div className="flex-align-start medium-gap">
          {selectable && (
            <div
              style={{ padding: '6px' }}
              onClick={(event) => {
                event.stopPropagation()
              }}
            >
              <Checkbox
                data-testid={`task-select-${dataTestId}`}
                checked={selected}
                aria-label={`Select task: ${title}`}
                onClick={(event) => {
                  event.stopPropagation()
                }}
                onChange={(event) => {
                  event.stopPropagation()
                  onSelect()
                }}
              />
            </div>
          )}
          <h2 style={{ padding: '6px 0' }} data-testid={`${dataTestId}-title`}>
            {isAiGenerated && <IconSparkles className="status-magical icon-svg-large" />}
            {title}
          </h2>
        </div>
        <Pill
          xs
          success={status === COMPLETED}
          warning={status === IGNORED}
          emphasized={status === READY_FOR_REVIEW}
          magical={status === 'collecting_evidence'}
        >
          {TASK_STATUS_MAP[status]}
        </Pill>
      </header>
      {description && (
        <section>
          <p data-testid={`${dataTestId}-description`}>{description}</p>
          {isAiGenerated && (
            <div className="mt">
              <PoweredByAI />
            </div>
          )}
        </section>
      )}
      <footer>
        <div className="task-card__metadata">
          <div>
            <InteractiveTooltip content={dateToUse.format('MMMM Do YYYY, h:mm:ss a')}>
              <small>
                {hasBeenUpdated ? 'Last updated' : 'Created'} {displayDate}
              </small>
            </InteractiveTooltip>
          </div>
          <div className="task-card__metadata--divider" />
          <Pill xs gradient={color || determineGradient(toNumber(index || 0))}>
            {agent}
          </Pill>
          <div className="task-card__metadata--divider" />
          <Pill xs>{pluralize(startCase(`related ${callType}`), callCount, true)}</Pill>

          {type === COACHING_TASK_TYPE && isArchived && (
            <>
              <div className="task-card__metadata--divider" />
              <Pill xs outlined success={agentReviewed} critical={!agentReviewed}>
                {agentReviewed ? 'Acknowledged' : 'Not Acknowledged'}
              </Pill>
            </>
          )}
          {createdBy ? (
            <>
              <div className="task-card__metadata--divider" />
              <Pill xs>
                <strong>{isAiGenerated ? 'Generated By:' : 'Created By:'}</strong>
                {isAiGenerated ? 'Balto AI' : createdBy}
              </Pill>
            </>
          ) : (
            <Pill magical xs>
              <IconSparkles /> AI
            </Pill>
          )}
        </div>
        <div className="flex-align-center flex-end small-gap" style={{ marginLeft: 'auto' }}>
          {handleSecondaryAction && (
            <Button
              data-testid="task-secondary-button"
              basic
              onClick={(event) => {
                event.stopPropagation()
                handleSecondaryAction(event)
              }}
            >
              {secondaryActionButtonText}
            </Button>
          )}
          {primaryActionButtonText && (
            <Button data-testid="drawer-trigger-button">{primaryActionButtonText}</Button>
          )}
        </div>
      </footer>
    </div>
  )
}
